import React, { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   grantOwnershipClaim,
   denyOwnershipClaim,
   type OwnershipClaim,
} from '@/api/vendo';
import type { VendoDevice } from '@/pages/Admin/Vendo';

/**
 * Phase 6 — the action an owner takes when the "suspected stolen" banner on a
 * device card is clicked. Same two outcomes as the Ownership Requests page,
 * but framed in plain operator language:
 *   - "I sold it"     → grant  (license transfers to the new account)
 *   - "Block — stolen" → deny  (hardware stays locked on the other account)
 * The location/time evidence (captured from the device's WireGuard endpoint)
 * helps the owner tell a real sale from a theft.
 */
const OwnershipActionModal: React.FC<{
   data: { device: VendoDevice; claim: OwnershipClaim };
}> = ({ data }) => {
   const { device, claim } = data;
   const { handleClose } = useUIStore();
   const queryClient = useQueryClient();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const [busy, setBusy] = useState<'grant' | 'deny' | null>(null);

   const act = async (action: 'grant' | 'deny') => {
      if (busy) return;
      setBusy(action);
      try {
         if (action === 'grant') await grantOwnershipClaim(claim._id);
         else await denyOwnershipClaim(claim._id);
         await queryClient.invalidateQueries({ queryKey: ['ownership-claims'] });
         await refetchDevices();
         toast.success(
            action === 'grant'
               ? `${device.name} handed over to the new account`
               : `${device.name} blocked — it stays locked on the other account`,
            { autoClose: 3000 }
         );
         handleClose();
      } catch (err) {
         const e = err as { response?: { data?: { error?: string } } };
         toast.error(e?.response?.data?.error || `Could not ${action}`);
         setBusy(null);
      }
   };

   const when = (() => {
      try {
         return new Date(claim.createdAt).toLocaleString();
      } catch {
         return claim.createdAt;
      }
   })();

   return (
      <Wrap>
         <Lead>
            Your device <b>{device.name}</b> was just registered on a different
            SmartFi account, so it is now <b>locked</b> there. Choose what should
            happen:
         </Lead>

         <Evidence>
            <Row>
               <Label>Hardware</Label>
               <Val>{claim.serial || claim.hardwareKey}</Val>
            </Row>
            <Row>
               <Label>Registered by</Label>
               <Val>{claim.claimantDeviceId}</Val>
            </Row>
            {claim.publicIp && (
               <Row>
                  <Label>From</Label>
                  <Val>
                     {claim.publicIp}
                     {claim.isp ? ` · ${claim.isp}` : ''}
                  </Val>
               </Row>
            )}
            <Row>
               <Label>When</Label>
               <Val>{when}</Val>
            </Row>
         </Evidence>

         <Hint>
            If this was a buyer you sold it to, allow it. If you didn't sell it,
            block it — the hardware stays locked on the other account.
         </Hint>

         <Actions>
            <BlockBtn disabled={!!busy} onClick={() => act('deny')}>
               {busy === 'deny' ? 'Blocking…' : 'Block it — it’s stolen'}
            </BlockBtn>
            <SoldBtn disabled={!!busy} onClick={() => act('grant')}>
               {busy === 'grant' ? 'Handing over…' : 'It’s OK — I sold it'}
            </SoldBtn>
         </Actions>
      </Wrap>
   );
};

export default OwnershipActionModal;

const Wrap = styled.div`
   padding: 4px 2px;
   max-width: 460px;
`;
const Lead = styled.p`
   margin: 0 0 14px;
   font-size: 14px;
   line-height: 1.55;
   color: ${colors.DARK};
`;
const Evidence = styled.div`
   border: 1px solid #f0a020;
   background: #fff7e6;
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 7px;
   margin-bottom: 14px;
`;
const Row = styled.div`
   display: flex;
   gap: 10px;
   font-size: 13px;
`;
const Label = styled.span`
   width: 96px;
   flex-shrink: 0;
   color: ${colors.GRAY};
`;
const Val = styled.span`
   color: ${colors.DARK};
   font-weight: 600;
   word-break: break-all;
`;
const Hint = styled.p`
   margin: 0 0 18px;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.GRAY};
`;
const Actions = styled.div`
   display: flex;
   gap: 10px;
   justify-content: flex-end;
   flex-wrap: wrap;
`;
const BlockBtn = styled.button`
   border: 1px solid #e57373;
   border-radius: 8px;
   padding: 10px 16px;
   font-size: 13px;
   font-weight: 600;
   color: #c62828;
   background: #fff;
   cursor: pointer;
   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }
`;
const SoldBtn = styled.button`
   border: none;
   border-radius: 8px;
   padding: 10px 16px;
   font-size: 13px;
   font-weight: 600;
   color: #fff;
   background: ${colors.PRIMARY};
   cursor: pointer;
   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }
`;
